/**
 * PWA Install Banner
 *
 * Shows a smart install prompt when the app can be installed (beforeinstallprompt)
 * or on iOS (shows manual instructions). Auto-hides if dismissed or already installed.
 */

import { useEffect, useState } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

const DISMISS_KEY = 'poaas_install_dismissed';
const DISMISS_DAYS = 14;

function wasDismissedRecently(): boolean {
  try {
    const ts = localStorage.getItem(DISMISS_KEY);
    if (!ts) return false;
    const diff = Date.now() - Number(ts);
    return diff < DISMISS_DAYS * 86_400_000;
  } catch {
    return false;
  }
}

function markDismissed(): void {
  try {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
  } catch {
    // Storage unavailable — banner may reappear next visit.
  }
}

function isStandalone(): boolean {
  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as unknown as { standalone?: boolean }).standalone === true
  );
}

function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as unknown as { MSStream?: unknown }).MSStream;
}

export function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIOS, setShowIOS] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone() || wasDismissedRecently()) return;

    // Chrome / Edge / Samsung — the browser fires beforeinstallprompt
    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // iOS Safari — no beforeinstallprompt, show manual instructions
    if (isIOS()) {
      setShowIOS(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  if (!visible) return null;

  const install = async () => {
    if (!deferredPrompt) return;
    await deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setVisible(false);
    }
    setDeferredPrompt(null);
  };

  const dismiss = () => {
    markDismissed();
    setVisible(false);
  };

  return (
    <div className="install-banner" role="alert">
      <div className="install-banner-icon">
        <img src="/icon-192.png" alt="" width="40" height="40" />
      </div>
      <div className="install-banner-body">
        <div className="install-banner-title">Install POAAS</div>
        <div className="install-banner-desc">
          {showIOS
            ? <>Tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong> for quick access.</>
            : 'Add to your home screen for a full-screen app experience.'
          }
        </div>
      </div>
      <div className="install-banner-actions">
        {!showIOS && deferredPrompt && (
          <button className="btn btn-primary btn-sm" onClick={install}>
            Install
          </button>
        )}
        <button
          className="btn btn-ghost btn-sm"
          onClick={dismiss}
          aria-label="Dismiss install banner"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
